import { useState } from "react";
import { useLang } from "../i18n";
import { Icon } from "./Icon";

const S = {
  desc: {
    es: "Tu mensaje llega directo a nuestro correo. Te respondemos lo antes posible.",
    en: "Your message goes straight to our inbox. We reply as soon as possible.",
  },
  name: { es: "Nombre", en: "Name" },
  email: { es: "Tu correo electrónico", en: "Your email" },
  muni: { es: "Municipio (opcional)", en: "Municipality (optional)" },
  message: { es: "Mensaje", en: "Message" },
  send: { es: "Enviar mensaje", en: "Send message" },
  sending: { es: "Enviando…", en: "Sending…" },
  success: { es: "¡Mensaje enviado! Te llegará una confirmación a tu correo.", en: "Message sent! A confirmation is on its way to your inbox." },
  error: { es: "No pudimos enviar el mensaje. Escríbenos directo a", en: "We could not send the message. Write us directly at" },
  namePh: { es: "Ej. María Rivera", en: "E.g. Maria Rivera" },
  muniPh: { es: "Ej. Ponce", en: "E.g. Ponce" },
  msgPh: { es: "Sugerencia, corrección de datos, pregunta…", en: "Suggestion, data correction, question…" },
};

const t = (o, lang) => o[lang] ?? o.es;
const EMAIL = "preparatepr78@gmail.com";

const inputCls =
  "w-full rounded-md border-2 border-slate-300 bg-white px-4 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:border-amber-600 focus:outline-none";

export function ContactForm() {
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", municipality: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!r.ok) throw new Error("send failed");
      setStatus("success");
      setForm({ name: "", email: "", municipality: "", message: "", website: "" });
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-success" className="rounded-md border border-green-700 bg-green-50 p-5 flex items-center gap-3">
        <Icon name="CheckCircle2" className="w-6 h-6 shrink-0 text-green-700" />
        <p className="text-base font-semibold text-green-900">{t(S.success, lang)}</p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="grid gap-4">
      <p className="text-sm text-slate-600">{t(S.desc, lang)}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.name, lang)} *</span>
          <input data-testid="contact-name" required maxLength={100} value={form.name} onChange={set("name")} placeholder={t(S.namePh, lang)} className={inputCls} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.email, lang)} *</span>
          <input data-testid="contact-email-input" type="email" required maxLength={120} value={form.email} onChange={set("email")} placeholder="tu@correo.com" className={inputCls} />
        </label>
      </div>
      <label className="block">
        <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.muni, lang)}</span>
        <input data-testid="contact-municipality" maxLength={80} value={form.municipality} onChange={set("municipality")} placeholder={t(S.muniPh, lang)} className={inputCls} />
      </label>
      <label className="block">
        <span className="mb-1.5 block text-sm font-bold text-slate-600">{t(S.message, lang)} *</span>
        <textarea data-testid="contact-message" required maxLength={2000} rows={4} value={form.message} onChange={set("message")} placeholder={t(S.msgPh, lang)} className={inputCls} />
      </label>
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      {status === "error" && (
        <p data-testid="contact-error" className="text-sm font-semibold text-red-700">
          {t(S.error, lang)} <a href={`mailto:${EMAIL}`} className="underline">{EMAIL}</a>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="contact-submit"
          type="submit"
          disabled={status === "sending"}
          className="inline-flex items-center gap-2 rounded-md bg-[#1C2B4A] px-6 py-3 text-base font-bold text-white hover:bg-amber-700 transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
        >
          <Icon name="ExternalLink" className="w-4 h-4" />
          {status === "sending" ? t(S.sending, lang) : t(S.send, lang)}
        </button>
        <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="text-sm font-semibold text-slate-500 hover:text-amber-700 transition-colors duration-200">
          {EMAIL}
        </a>
      </div>
    </form>
  );
}
